import LandingSection from '@/components/landing/LandingSection'
import JsonLd from '@/components/JsonLd'
import { getFAQPageSchema, getBreadcrumbSchema } from '@/utils/schemas'
import { ChevronDown, ChevronUp } from 'react-feather'
import { useState } from 'react'
import Link from 'next/link'

const faqs = [
  {
    question: 'What is Annotate.tv?',
    answer:
      'Annotate.tv is a web-based video annotation tool that lets you take timestamped notes on YouTube videos, chat with videos using AI, extract text from slides using OCR, and export your notes to PDF, Notion, Evernote, Obsidian, and Readwise.',
  },
  {
    question: 'How much does Annotate.tv cost?',
    answer:
      'Annotate.tv offers a free Starter plan that includes up to 5 videos with unlimited notes. The Premium plan is $5/month (or $4/month when billed annually) and includes unlimited videos, transcript access, AI chat, text extraction from slides, and all export options.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Yes, Annotate.tv offers a 14-day free trial of Premium features. No credit card is required to start the trial, and you can cancel anytime.',
  },
  {
    question: 'Can I chat with a YouTube video using AI?',
    answer:
      'Yes, Annotate.tv Premium includes an AI chat feature that lets you ask questions about the video and get instant answers based on the video transcript. This helps you find specific information without rewinding or searching manually.',
  },
  {
    question: 'What export formats does Annotate.tv support?',
    answer:
      'Annotate.tv supports exporting notes to PDF, Markdown (compatible with Notion, Evernote, and Obsidian), and direct sync with Readwise. This lets you read and organize your notes in your preferred knowledge management tool.',
  },
  {
    question: 'Does Annotate.tv work with YouTube videos?',
    answer:
      'Yes, Annotate.tv is designed primarily for YouTube videos. Simply paste a YouTube link to start taking timestamped notes. Annotate.tv also supports Vimeo videos.',
  },
  {
    question: 'What is the text from video feature?',
    answer:
      'The text from video feature (OCR) lets you extract text directly from video slides or frames. Instead of manually typing out text shown in a presentation or lecture, you can capture it with one click.',
  },
  {
    question: 'Can I share my annotated videos?',
    answer:
      'Yes, Annotate.tv lets you create public links to share your annotated videos with others. This is useful for educators sharing lecture notes with students or teams collaborating on video content.',
  },
  {
    question: 'How do timestamped notes work?',
    answer:
      'When you take a note in Annotate.tv, it automatically saves the timestamp of the video at that moment. Clicking on any note later will jump directly to that point in the video, making it easy to review specific sections.',
  },
  {
    question: 'Is there a student or teacher discount?',
    answer:
      'Yes, Annotate.tv offers discounted pricing for students and teachers who cannot afford the regular price. Contact mati@annotate.tv with your academic email to inquire about educational discounts.',
  },
]

const breadcrumbs = [
  { name: 'Home', path: '/' },
  { name: 'FAQ', path: '/faq' },
]

export default function FAQ() {
  return (
    <LandingSection
      title="Frequently Asked Questions"
      description="Find answers to common questions about Annotate.tv, pricing, features, and how to get started with video annotation."
      canonical="/faq"
    >
      <JsonLd
        data={[getFAQPageSchema(faqs), getBreadcrumbSchema(breadcrumbs)]}
      />
      <div className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
              Frequently Asked Questions
            </h1>
            <p className="mt-4 text-xl text-gray-600">
              Everything you need to know about Annotate.tv
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Still have questions?{' '}
              <a
                href="mailto:mati@annotate.tv"
                className="text-primary font-medium hover:underline"
              >
                Contact us
              </a>
            </p>
          </div>

          <div className="mt-16 bg-gray-50 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Ready to get started?
            </h2>
            <p className="mt-2 text-gray-600">
              Start taking notes on YouTube videos for free
            </p>
            <Link href="/signup">
              <a className="mt-4 inline-block bg-primary text-white px-6 py-3 rounded-lg font-medium clickable-opacity">
                Start Free
              </a>
            </Link>
          </div>
        </div>
      </div>
    </LandingSection>
  )
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span className="text-lg font-medium text-gray-900">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-gray-600">{answer}</p>
        </div>
      )}
    </div>
  )
}
